import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Button, Card, Flex, Input, List, Modal, notification, Space, Typography } from "antd";
import './index.scss';
import { EditOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import SearchUsers from "../component/SearchUser";
import SearchStatus from "../component/SearchStatus";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { postData, putData } from "../../../../../actions/developer";
import { getData } from "../../../../../actions/apiCall";
import { MasterConfigs } from "../../../../../interfaces/entity";
import FormSetup from "../../../../../components/Forms/FormSetup";
import { title } from "process";
const { confirm } = Modal;

const { Title, Text } = Typography;

interface Task {
    _id: string;
    title: string;
    status: string;
    order?: number;
    sprintId?: string;
    columnId?: string;
    assignee?: string
}

interface SprintList {
    _id: string;
    name: string;
    tasks: Task[];
    startDate?: string;
    endDate?: string;
    projectId?: string;
    order?: number;

}

interface TaskListProps {
    projectId: string;
    loadData: boolean;
    onLoaded: () => void;
    config: MasterConfigs;
    onClickIssue: (e: any) => void;
    onSearch: string[];
    showOldSprint?: boolean;
}

const DragDropTaskManager: React.FC<TaskListProps> = ({ loadData, onLoaded, projectId, config, onClickIssue, onSearch, showOldSprint }) => {
    const [data, setData] = useState([] as SprintList[]);
    const [isOpen, setOpen] = useState(false);
    const [id, setId] = useState(null);
    const [columnData, setColumnData] = useState([] as any[]);
    const [dragIsInProgress, setDragIsInProgress] = useState(false);

    const sprintData = showOldSprint ? { pastSprint: true } : {}

    const editConfig: MasterConfigs = {
        ...config,
        onRefresh: () => {
            setOpen(false);
            getSprintList();
            setId(null);
        },
    }

    useEffect(() => {
        setOpen(false);
        getSprintList();
        setId(null);
    }, [onSearch, showOldSprint]);

    const onEdit = (list: SprintList) => {
        setId(list._id);
    }

    useEffect(() => {
        if (id) {
            setOpen(true);
        }
    }, [id])


    const [activeId, setActive] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        getSprintList();
        getColumnData();
    }, []);

    useEffect(() => {
        if (loadData) {
            getSprintList();
        }
    }, [loadData, projectId]);

    const sort = (a, b) => a.order - b.order;

    const getSprintList = async () => {
        const resData = await dispatch(getData(`/sprints/${projectId}`, { teamMember: onSearch, ...sprintData }));
        if (resData) {
            resData.data.forEach((list) => {
                list.tasks.sort(sort);
            });
            setData(resData.data.sort(sort));
            onLoaded()
        }
    }

    const onCreateTask = async (value, list: SprintList) => {
        if (!value.target.value || value.target.value === '') {
            return
        }
        const resData = await dispatch(postData(`/tasks`, {
            projectId: list.projectId,
            sprintId: list._id,
            title: value.target.value,
            order: list.tasks.length
        }));
        if (resData) {
            list.tasks.push(resData.data);
            setData([...data]);
            setActive(null);
        }
    };

    const onStartSprint = async (list: SprintList) => {
        const resData = await dispatch(putData(`/sprints/${projectId}/${list._id}`, {
            sprintStatus: 'Active',
            startDate: new Date()
        }));
        if (resData) {
            getSprintList();
        }
    }

    const confirmStartSprint = (list: SprintList) => {
        confirm({
            title: 'Do you want to start this sprint?',
            content: 'The current sprint will be closed, all pending tasks will be moved to this sprint, and this action cannot be undone.',
            icon: <ExclamationCircleOutlined />,
            onOk() {
                onStartSprint(list);
            },
            onCancel() {
                console.log('Cancel');
            },
        });
    }

    const onClose = () => {
        setOpen(false);
        setId(null);
    }

    const updateTaskOrder = async (orderData) => {
        const tasks = orderData.map((column) => column.tasks.map(ele => ({ ...ele, sprintId: column._id }))).flat();
        const resData = await dispatch(putData(`/tasks/bulk/update`, tasks));
        if (resData) {
            getSprintList();
        }
    }

    const getColumnData = async () => {
        const resData = await dispatch(getData(`/columns/${projectId}`));
        if (resData.code == 200) {
            setColumnData(resData.data.sort(sort));
        }
    }

    const updateTaskStatus = async (value, task) => {
        if (!value || value === task.title || value === '') {
            return
        }
        const resData = await dispatch(putData(`/tasks/${task._id}`, { title: value }));
        if (!resData) {
            notification.error({ message: 'Error', description: 'Failed to update task status' });
        } else {
            getSprintList();
        }
    }

    const handleDragEnd = (result: any) => {
        const { source, destination } = result;
        if (!destination) return;
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }
        const updatedTasks = [...data];

        const sourceListId = source.droppableId;
        const destinationListId = destination.droppableId;

        const sourceList = updatedTasks.find((list) => list._id === sourceListId)!;
        const destinationList = updatedTasks.find((list) => list._id === destinationListId)!;

        const [movedTask] = sourceList.tasks.splice(source.index, 1);

        if (sourceListId === destinationListId) {
            sourceList.tasks.splice(destination.index, 0, { ...movedTask, sprintId: destinationListId });
        } else {
            destinationList.tasks.splice(destination.index, 0, { ...movedTask, sprintId: destinationListId });
        }

        destinationList.tasks.forEach((task, index) => {
            task.order = index;
        });
        setData([...updatedTasks]);
        updateTaskOrder(updatedTasks);
        setDragIsInProgress(false);
    };

    const onDragStart = () => {
        setDragIsInProgress(true);
    }

    const handleEditStart = (event) => {
        event.stopPropagation();
    };

    const handleClick = (id) => {
        onClickIssue(id);
    };

    return (
        <>
            <FormSetup config={editConfig} id={id} isModal={true} open={isOpen} onClose={onClose} />
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={onDragStart}>
                <div className="issues_create_box">
                    {data.map((list: SprintList) => (
                        <Space direction="vertical" size={"middle"} style={{ width: '100%' }}>
                            <Droppable droppableId={list._id} key={list._id}>
                                {(provided) => (
                                    <div>
                                        <Flex justify="space-between" align="center">
                                            <Title level={5}>{list.name}</Title>
                                            {list?.startDate && <Space>
                                                {!showOldSprint && <Button type="link" onClick={() => confirmStartSprint(list)}>Start Now</Button>}
                                                <Text>{dayjs(list?.startDate).format('DD MMM YYYY')} - {dayjs(list?.endDate).format('DD MMM YYYY')}</Text>
                                                {!showOldSprint && <Button icon={<EditOutlined />} onClick={() => onEdit(list)} />}
                                            </Space>
                                            }
                                        </Flex>
                                        <div
                                            className="issues_view"
                                            id={'issues_view'}
                                            ref={provided.innerRef}>
                                            {list.tasks.map((task, index) => (
                                                <Draggable
                                                    draggableId={task._id}
                                                    index={index}
                                                    key={task._id}
                                                >
                                                    {(provided) => (
                                                        <div
                                                            className="issue"
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <Flex justify="space-between" align="center" onClick={() => handleClick(task?._id)}>
                                                                <div
                                                                    onClick={handleEditStart}
                                                                    style={{ display: 'inline-block', cursor: 'pointer' }}
                                                                >
                                                                    <Text editable={{
                                                                        onChange: (e) => {
                                                                            e !== task.title && updateTaskStatus(e, task)
                                                                        }
                                                                    }}>{task.title}</Text>
                                                                </div>
                                                                <div
                                                                    onClick={handleEditStart}
                                                                    style={{ display: 'inline-block', cursor: 'pointer' }}
                                                                >
                                                                    <Space direction="horizontal">
                                                                        <SearchStatus columnData={columnData} task={task} />
                                                                        <SearchUsers projectId={projectId} taskId={task._id} selectedMember={task?.assignee} onLoad={() => getSprintList()} />
                                                                    </Space>
                                                                </div>
                                                            </Flex>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {!dragIsInProgress && activeId != list._id && !showOldSprint && <Button type="text" style={{
                                                marginTop: '10px'
                                            }} icon={<PlusOutlined />} onClick={() => setActive(list._id)}>Create Issue</Button>}
                                            {!dragIsInProgress && activeId == list._id && <div className="create_task">
                                                <Input autoFocus placeholder="What needs to be done?" onPressEnter={(e) => onCreateTask(e, list)} />
                                            </div>}
                                        </div>
                                    </div>
                                )}
                            </Droppable>
                        </Space>
                    ))}
                </div>
            </DragDropContext >
        </>
    );
};

export default DragDropTaskManager;
